/**
 * Practice overview (P5) — navigation first, then performance + guidance.
 * Canvas mockup was exploration (pathway chips + side-by-side Do next / Eligibility);
 * live page follows that hierarchy without duplicating the same facts.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  LineChart,
  SlidersHorizontal,
  TrendingUp,
  UploadCloud,
} from 'lucide-react';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useMipsScores, usePerformanceSummaries, usePracticeSettings } from '@/hooks/queries/useMipsQueries';
import {
  useEligibilityPulse,
  useMvpSelections,
  useNpiProfiles,
  useResolvedFinancials,
  useSubgroups,
} from '@/hooks/queries/useMvpQueries';
import { YearLockBanner } from '@/components/gap-management/shared/YearLockBanner';
import { UnifiedScoreCard } from '@/components/gap-management/shared/UnifiedScoreCard';
import { MipsDataService } from '@/lib/mips-data-service';
import { scoreQualityMeasure } from '@/lib/mips-scoring';
import { topOpportunities } from './overview-logic';

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface SummaryRow {
  measureId: string;
  submissionMethod: string;
  isMultiStrata: boolean;
  counts: { ip: number; ex: number; den: number; met: number; exc: number; not: number };
}

interface Scores {
  quality_score?: number;
  pi_score?: number;
  ia_score?: number;
  cost_score?: number;
  composite_mips_score?: number;
  calculation_method?: string;
}

interface OpportunityRow {
  measureId: string;
  title: string;
  currentPoints: number;
  potentialPoints: number;
  performanceRate: number;
}

interface AttentionRow {
  measureId: string;
  title: string;
  reason: string;
}

interface DoNextItem {
  id: string;
  label: string;
  detail: string;
  href: string;
}

export function PracticeOverview({ practice }: { practice: Practice }) {
  const navigate = useNavigate();
  const { programYear } = useProgramYear();
  const hasMvp = MipsDataService.getInstance().hasMvpDataset(programYear);

  const scoresQ = useMipsScores(practice.id, programYear);
  const summariesQ = usePerformanceSummaries(practice.id, programYear);
  const settingsQ = usePracticeSettings(practice.id, programYear);
  const selectionsQ = useMvpSelections(hasMvp ? practice.id : null, programYear);
  const subgroupsQ = useSubgroups(hasMvp ? practice.id : null, programYear);
  const profilesQ = useNpiProfiles(hasMvp ? practice.id : null, programYear);
  const financialsQ = useResolvedFinancials(hasMvp ? practice.id : null, programYear);
  const pulseQ = useEligibilityPulse(hasMvp ? practice.id : null, programYear, 'within_year');

  const scores: Scores | null = scoresQ.data ?? null;
  const summaries: SummaryRow[] | null =
    !summariesQ.data && summariesQ.isPending
      ? null
      : (Array.isArray(summariesQ.data) ? (summariesQ.data as SummaryRow[]) : []);
  const loading =
    (!scoresQ.data && scoresQ.isPending) ||
    (!summariesQ.data && summariesQ.isPending) ||
    (!settingsQ.data && settingsQ.isPending);
  // Opportunities must score with the practice's real size (small practices
  // earn 3 pts, not 0, on completeness/case-minimum failures).
  const practiceSize: 'small' | 'large' | null =
    !settingsQ.data && settingsQ.isPending ? null : (settingsQ.data?.is_small_practice ? 'small' : 'large');

  const selections = selectionsQ.data ?? [];
  const subgroups = subgroupsQ.data ?? [];
  const profiles = profilesQ.data;
  const pulse = pulseQ.data;
  const materialCount = pulse?.summary?.materialCount ?? pulse?.totalEvents ?? null;

  const { attention, opportunities } = useMemo(() => {
    const attention: AttentionRow[] = [];
    const opps: OpportunityRow[] = [];
    if (!summaries || !practiceSize) return { attention, opportunities: opps };
    const svc = MipsDataService.getInstance();

    for (const row of summaries) {
      if (row.isMultiStrata) continue;
      const { den, met, exc, not: notMet } = row.counts;
      if (den === 0 && met === 0 && notMet === 0) continue;

      const measure = svc.getActiveMeasure(row.measureId);
      const title = measure?.title ?? row.measureId;
      const benchmark = svc.getBenchmarksForMeasure(row.measureId)
        .find(b => b.submissionMethods === row.submissionMethod) ?? null;

      const current = scoreQualityMeasure({
        denominator: den, performanceMet: met, performanceNotMet: notMet,
        numeratorExceptions: exc, practiceSize, benchmark,
      });
      const potential = scoreQualityMeasure({
        denominator: den, performanceMet: met + notMet, performanceNotMet: 0,
        numeratorExceptions: exc, practiceSize, benchmark,
      });

      if (!current.passesCompleteness) {
        attention.push({
          measureId: row.measureId,
          title,
          reason: `Data completeness ${current.dataCompletenessRate.toFixed(1)}% (needs 75%)`,
        });
      } else if (!current.meetsCaseMinimum) {
        attention.push({
          measureId: row.measureId,
          title,
          reason: `${den} cases (needs 20)`,
        });
      }

      if (potential.score > current.score) {
        opps.push({
          measureId: row.measureId,
          title,
          currentPoints: current.score,
          potentialPoints: potential.score,
          performanceRate: current.performanceRate,
        });
      }
    }
    return { attention, opportunities: topOpportunities(opps, 5) };
  }, [summaries, programYear, practiceSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const pathway = useMemo(() => {
    if (!hasMvp) return null;
    const svc = MipsDataService.getInstance();
    const awaitingRegister = selections.filter(s => !s.registered).length;
    return {
      empty: selections.length === 0,
      items: selections.map(s => {
        const def = svc.getMvp(s.mvp_id, programYear);
        const entity =
          s.entity_type === 'subgroup'
            ? (s.subgroup_name || `Subgroup #${s.subgroup_id}`)
            : 'Group';
        return {
          mvpId: s.mvp_id,
          title: def?.title ?? s.mvp_id,
          shortTitle: (def?.clinicalTopic || def?.title || s.mvp_id).slice(0, 42),
          entity,
          registered: s.registered,
        };
      }),
      subgroupCount: subgroups.length,
      awaitingRegister,
    };
  }, [hasMvp, selections, subgroups.length, programYear]);

  // Do next = actions NOT already owned by Pathway / Eligibility strips
  const doNext: DoNextItem[] = useMemo(() => {
    const items: DoNextItem[] = [];
    if (hasMvp && selections.length === 0) {
      items.push({
        id: 'pick-mvp',
        label: 'Explore MVPs',
        detail: 'See if a Value Pathway fits this practice',
        href: '/measure-selection#mvps', // Configuration keep-mount honors hash
      });
    }
    if (hasMvp && profiles && (profiles.unmatchedNpis ?? 0) > 0) {
      items.push({
        id: 'org-match',
        label: 'Resolve unmatched clinicians',
        detail: `${profiles.unmatchedNpis} NPI(s) need org confirmation`,
        href: '/measure-selection#qpp-reports',
      });
    }
    if (attention.length > 0) {
      items.push({
        id: 'gaps',
        label: 'Close quality gates',
        detail: `${attention.length} measure(s) failing completeness or case minimum`,
        href: '/programs/mips/gap-management/quality',
      });
    }
    if (opportunities.length > 0) {
      items.push({
        id: 'opps',
        label: 'Pursue top opportunities',
        detail: `${opportunities.length} measure(s) with open-gap point upside`,
        href: '/programs/mips/gap-management/quality',
      });
    }
    if (items.length === 0) {
      items.push({
        id: 'gap-mgmt',
        label: 'Open Gap Management',
        detail: 'Review scores and patient-level gaps',
        href: '/programs/mips/gap-management/quality',
      });
    }
    return items.slice(0, 4);
  }, [hasMvp, selections.length, profiles, attention.length, opportunities.length]);

  const name = practice.practice_alias || practice.tin_name;
  const categoryCards: Array<{ label: string; value: number | undefined }> = [
    { label: 'Quality', value: scores?.quality_score },
    { label: 'PI', value: scores?.pi_score },
    { label: 'IA', value: scores?.ia_score },
    { label: 'Cost', value: scores?.cost_score },
  ];

  const chargesResolved = financialsQ.data?.tin?.resolved;
  const chargesSource = financialsQ.data?.tin?.source;
  const groupElig = profiles?.affiliationInsights?.groupEligible;
  const practiceGroupEligible = profiles?.practiceEligibility?.mipsEligible;

  return (
    <div>
      <YearLockBanner />
      <header className="mb-6">
        <h1 className="text-3xl font-bold">{name}</h1>
        <p className="text-muted-foreground">
          MIPS overview · TIN {practice.tin} · performance year {programYear}
        </p>
      </header>

      <div className="mb-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Navigate
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Button
            variant="outline"
            className="justify-between"
            onClick={() => navigate('/programs/mips/gap-management/quality')}
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Gap Management
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="justify-between"
            onClick={() => navigate('/measure-selection')}
          >
            <span className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" /> Configuration
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="justify-between"
            onClick={() => navigate('/insights/eligibility-changes')}
          >
            <span className="flex items-center gap-2">
              <LineChart className="h-4 w-4" /> Insights
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="justify-between"
            onClick={() => navigate('/upload')}
          >
            <span className="flex items-center gap-2">
              <UploadCloud className="h-4 w-4" /> Upload Data
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Loading overview…
        </div>
      ) : (
        <div className="space-y-6">
          {/* 1. Performance first */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <UnifiedScoreCard
                compositeScore={Number(scores?.composite_mips_score ?? 0)}
                onViewReport={() => navigate('/insights/payment-adjustment')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 lg:col-span-2">
              {categoryCards.map(c => (
                <Card key={c.label}>
                  <CardHeader className="pb-2">
                    <CardDescription>{c.label}</CardDescription>
                    <CardTitle className="text-3xl">
                      {c.value != null ? Number(c.value).toFixed(1) : '—'}
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>

          {/* 2. Compact pathway strip (registration lives here only) */}
          {pathway && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Pathway
              </p>
              {pathway.empty ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Traditional MIPS</p>
                    <p className="text-xs text-muted-foreground">
                      No MVP selected · explore Value Pathways in Configuration
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate({ pathname: '/measure-selection', hash: 'mvps' })}
                  >
                    Explore MVPs
                  </Button>
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {pathway.items.map(item => (
                    <button
                      key={`${item.mvpId}-${item.entity}`}
                      type="button"
                      onClick={() => navigate({ pathname: '/measure-selection', hash: 'mvps' })}
                      className="rounded-lg border bg-background px-3 py-3 text-left transition-colors hover:bg-muted/40"
                    >
                      <p className="text-sm font-semibold">
                        {item.mvpId} · {item.shortTitle}
                        {item.shortTitle.length >= 42 ? '…' : ''}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.entity}</p>
                      <div className="mt-2">
                        <Badge
                          variant="outline"
                          className={
                            item.registered
                              ? 'border-teal-300 bg-teal-50 text-teal-800'
                              : 'border-amber-300 bg-amber-50 text-amber-900'
                          }
                        >
                          {item.registered ? (
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Registered
                            </span>
                          ) : (
                            'Selected · not registered'
                          )}
                        </Badge>
                      </div>
                    </button>
                  ))}
                  <div className="rounded-lg border bg-muted/20 px-3 py-3">
                    <p className="text-sm font-semibold">
                      {pathway.subgroupCount} subgroup{pathway.subgroupCount === 1 ? '' : 's'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {pathway.awaitingRegister > 0
                        ? `${pathway.awaitingRegister} awaiting Registered mark`
                        : 'All selected pathways marked Registered'}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 h-7 px-0"
                      onClick={() => navigate({ pathname: '/measure-selection', hash: 'mvps' })}
                    >
                      Manage in Configuration
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 3. Do next | Eligibility side-by-side — no duplicate facts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardList className="h-4 w-4" />
                  Do next
                </CardTitle>
                <CardDescription>Actions not already shown above</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {doNext.map((item, idx) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => navigate(item.href)}
                        className="flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50"
                      >
                        <span className="flex gap-2">
                          <Badge variant="outline" className="h-5 w-5 shrink-0 justify-center p-0 text-[10px]">
                            {idx + 1}
                          </Badge>
                          <span>
                            <span className="font-medium">{item.label}</span>
                            <span className="block text-xs text-muted-foreground">{item.detail}</span>
                          </span>
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {hasMvp ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Eligibility & data</CardTitle>
                  <CardDescription>CMS freshness · material pulse lives here once</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">CMS roster</span>
                    <span>
                      {profiles?.lastHydrated
                        ? `Refreshed ${new Date(profiles.lastHydrated).toLocaleDateString()}`
                        : 'Not hydrated yet'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Group eligibility</span>
                    <span>
                      {practiceGroupEligible === true
                        ? 'Eligible'
                        : practiceGroupEligible === false
                          ? 'Not eligible'
                          : groupElig != null
                            ? `${groupElig} NPIs group-eligible`
                            : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Within-year changes</span>
                    <button
                      type="button"
                      className="font-semibold underline-offset-2 hover:underline"
                      onClick={() => navigate('/insights/eligibility-changes')}
                    >
                      {materialCount == null && pulseQ.isPending
                        ? '…'
                        : `${materialCount ?? 0} material`}
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Unmatched NPIs</span>
                    <span>{profiles?.unmatchedNpis ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Allowed charges basis</span>
                    <span className="text-right text-xs sm:text-sm">
                      {chargesResolved != null
                        ? `$${Math.round(chargesResolved).toLocaleString()}${chargesSource ? ` · ${chargesSource}` : ''}`
                        : '—'}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/insights/eligibility-changes')}
                  >
                    Open Insights pulse
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Eligibility & data</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  MVP eligibility pulse available in years with a Value Pathways dataset
                  (viewing PY{programYear}).
                </CardContent>
              </Card>
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Needs attention
                </CardTitle>
                <CardDescription>Measures failing a scoring gate</CardDescription>
              </CardHeader>
              <CardContent>
                {attention.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No gate failures on reported measures.</p>
                ) : (
                  <ul className="space-y-2">
                    {attention.slice(0, 6).map(a => (
                      <li key={a.measureId} className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          {a.measureId} · {a.title.slice(0, 40)}{a.title.length > 40 ? '…' : ''}
                        </span>
                        <Badge variant="outline" className="text-amber-600">{a.reason}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  Top opportunities
                </CardTitle>
                <CardDescription>Largest point gains if open gaps close</CardDescription>
              </CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No open-gap point opportunities found.</p>
                ) : (
                  <ul className="space-y-2">
                    {opportunities.map(o => (
                      <li key={o.measureId} className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          {o.measureId} · {o.title.slice(0, 40)}{o.title.length > 40 ? '…' : ''}
                        </span>
                        <span className="tabular-nums text-emerald-700">
                          {o.currentPoints.toFixed(1)} → {o.potentialPoints.toFixed(1)} pts
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
